/**
 * Site map of the student app: every page a student can reach, with the
 * label and one-line description the menus show (`label`, `meta`) and a
 * fuller description of what the page is for (`detail`).
 *
 * The side menus (SidePanelNav, CourseSideNav, ConfSideNav) read `label`,
 * `icon` and `meta` from here, so the published map can't drift from what
 * students see. scripts/help-center-json.mjs publishes it in
 * help-center.json, where the v2 support "Draft with AI" reads it to
 * describe where things are.
 *
 * `path` is the hash route (https://app.my247ed.com/#<path>); `:id` marks a
 * per-enrollment or per-registration page. `audience` says when a page is
 * shown, if not always.
 */

export const SITE_SECTIONS = [
    {key: 'dashboard', label: 'Dashboard', detail: 'Home page: what needs attention, courses to continue, conferences, payment plans and shortcuts.'},
    {key: 'courses', label: 'Courses', detail: 'Online course enrollments: current and completed courses, the course catalog, bundles and reactivation.'},
    {key: 'conferences', label: 'Conferences', detail: 'In-person, virtual and on-demand conferences, clinics and trainings: registering and the records of ones attended.'},
    {key: 'pd', label: 'Professional Development', detail: 'Washington clock-hour planning: renewal progress, salary schedule placement and clock-hour history. Shown only while Washington clock hours is turned on.'},
    {key: 'records', label: 'Certificates & Forms', detail: 'Every certificate and form: applications in progress, completed certificates to view or download, applications available to start, receipts and the study activity calendar.'},
    {key: 'account', label: 'Your Account', detail: 'Profile, login, billing, notifications, work schedule and certification IDs (the gear icon in the top bar, labelled Settings on desktop).'},
    {key: 'support', label: 'Support', detail: 'The Help Center (searchable answers) and support tickets.'},
];

export const SITE_PAGES = {
    // ── Courses ──────────────────────────────────────────────────────────
    currentEnrollments: {
        section: 'courses', label: 'Current Enrollments', icon: 'fa-circle-play', path: '/enrollments',
        meta: "Access, review and log time for the courses you're working on.",
        detail: 'Course cards for every course in progress, with the next step (Set Up Study Plan, Write Reflection, Finish Course, Apply for Certificate, Continue), the expiration date and buttons such as Open Course, Log Time and Certificates & Forms.',
    },
    completedEnrollments: {
        section: 'courses', label: 'Completed Enrollments', icon: 'fa-check-to-slot', path: '/enrollments/completed',
        meta: 'Finished courses, archived by certification and year.',
        detail: 'Finished courses in year tabs, grouped by certification; reactivated older courses are listed under Reactivated Courses.',
    },
    reactivateCourses: {
        section: 'courses', label: 'Reactivate Courses', icon: 'fa-redo', path: '/reactivation',
        meta: 'Restore access to expired course enrollments.',
        audience: 'only while the student has expired courses',
        detail: 'Reactivate expired courses: Completed Hours Only (free, reopens the course to finish the hours already logged), Full Course or Partial Hours (per-hour fee). A course expired within the last 5 days can be reactivated free for 5 more days; courses expired over a year become a new enrollment.',
    },
    courseCatalog: {
        section: 'courses', label: 'Online Course Catalog', icon: 'fa-browsers', path: '/course-catalog',
        meta: 'Browse online courses and offerings.',
        detail: 'Every online course, browsed by certification then category. Includes the Access a Training Course box for course access codes (not a search box). Course pages show the description, goals, outline and an Enroll button.',
    },
    buildBundle: {
        section: 'courses', label: 'Build a Bundle', icon: 'fa-layer-group', path: '/course-catalog/bundle',
        meta: 'Choose several courses and enroll in them together.',
        detail: 'Course Bundle Builder for clock-hour users: size a block of clock hours, pick courses and pay or finance it. Also the Work With a Representative request form.',
    },
    cartCheckout: {
        section: 'courses', label: 'Cart Checkout', icon: 'fa-shopping-cart', path: '/cart-checkout',
        meta: 'Finish enrolling in the courses waiting in your cart.',
        audience: 'only when a representative has prepared a cart',
        detail: 'Review and sign for courses a representative prepared, to add them to the account.',
    },

    // ── Conferences ──────────────────────────────────────────────────────
    conferenceRecords: {
        section: 'conferences', label: 'Conference Records', icon: 'fa-screen-users', path: '/registrations',
        meta: "Manage the in-person or virtual conferences or trainings you've attended.",
        detail: 'Every conference, clinic and Coaches School registration, by year, with records needing attention first. Open one to log attendance and apply for credit.',
    },
    conferenceCenter: {
        section: 'conferences', label: 'Conference Center', icon: 'fa-presentation', path: '/conference-center',
        meta: 'Register or get credit for a conference or remote content.',
        detail: 'Upcoming and open conferences to register for, including WIAA hands-on clinics and Coaches School. Register Now takes a registration type, an optional Access/Discount Code, and card or purchase order payment.',
    },

    // ── Professional Development (clock hours only) ──────────────────────
    pdOverview: {
        section: 'pd', label: 'Overview', icon: 'fa-location-crosshairs', path: '/professional-development',
        meta: "Where you stand: hours earned, in progress, and what's next.",
        audience: 'Washington clock-hour users',
        detail: 'Clock-hour summary: details on file, hours earned and in progress, and clock hours by year.',
    },
    pdSalary: {
        section: 'pd', label: 'Salary Schedule', icon: 'fa-chart-line-up', path: '/professional-development/salary',
        meta: "Your district's schedule, your placement, and what the next column is worth.",
        audience: 'Washington clock-hour users',
        detail: "The district's salary schedule with the student's placement, the next column and what it is worth. The district's salary office is the final word on hours needed.",
    },
    pdRenewal: {
        section: 'pd', label: 'Renewal Progress', icon: 'fa-calendar-check',
        path: '/professional-development/renewal',
        meta: 'Hours counted toward your certificate renewal, and your full clock-hour history.',
        audience: 'Washington clock-hour users',
        detail: 'Hours toward the next certificate renewal date and the full clock-hour history.',
    },
    pdSettings: {
        section: 'pd', label: 'Settings', icon: 'fa-sliders', path: '/professional-development/settings',
        meta: 'Your placement, renewal date, OSPI number and work schedule.',
        audience: 'Washington clock-hour users',
        detail: 'District, school, years teaching, degree, clock-hour column, renewal date, OSPI certificate number and contracted work schedule.',
    },
    workWithRep: {
        section: 'pd', label: 'Work With a Representative', icon: 'fa-phone', path: '/course-catalog/bundle?phase=lead',
        meta: 'Have a representative plan your hours and handle enrollment for you.',
        detail: 'Request form for a representative to plan clock hours and enroll the student.',
    },

    // ── Certificates & Forms ─────────────────────────────────────────────
    inProgressApplications: {
        section: 'records', label: 'In Progress Applications', icon: 'fa-tasks', path: '/reports/applications',
        meta: "Applications you've started for certifications and completion forms.",
        audience: 'listed only while an application is in progress',
        detail: 'Started applications and what each needs: revise, pay the fee, or wait for review.',
    },
    completedCertificates: {
        section: 'records', label: 'Completed Certificates', icon: 'fa-file-certificate', path: '/reports/completed',
        meta: 'Approved certificates and official forms, ready to view or print.',
        detail: 'Everything awarded, in year tabs by certification, with download buttons for PDF forms such as the FORM SPI 1125. WIAA coaching certifications are listed to confirm they were awarded, but they are not downloaded: they sync digitally to FinalForms (button: View Certification Status at FinalForms).',
    },
    availableApplications: {
        section: 'records', label: 'Available Applications', icon: 'fa-file-circle-plus', path: '/reports/available',
        meta: "Applications and forms your courses and conferences offer that you haven't started.",
        detail: 'Applications not started yet, split into Ready to Apply and Not Ready Yet, with a status saying what is missing (More Time Needed, Finish Coursework, Reflection In Review, Study Plan Needed, Payment Needed, Certification Off, Enrollment Expired).',
    },
    receipts: {
        section: 'records', label: 'Receipts', icon: 'fa-file-invoice', path: '/reports/receipts',
        meta: 'Payment receipts for your courses, conferences, and application fees.',
        detail: 'Every card payment (courses, bundles, reactivations, conferences, application fees) with View Receipt, plus itemized receipts and invoices staff have issued.',
    },
    studyActivity: {
        section: 'records', label: 'Study Activity', icon: 'fa-calendar-days', path: '/activity',
        meta: 'See all of your logged time across courses and conferences in one calendar.',
        detail: 'Calendar of all logged time across courses and conferences; flags unusual days, overlapping entries and time inside the contracted work schedule.',
    },

    // ── Your Account ─────────────────────────────────────────────────────
    accountSettings: {
        section: 'account', label: 'Account Settings', icon: 'fa-user-gear', path: '/account',
        meta: 'Personal and contact information.',
        detail: 'Name, date of birth, address and phone. Name changes are made by staff (e-mail support@my247ed.com with a photo of updated ID).',
    },
    loginCredentials: {
        section: 'account', label: 'Login Credentials', icon: 'fa-lock-keyhole', path: '/account/login',
        meta: 'Email, phone and password used to sign in.',
        detail: 'Primary and secondary e-mail (both can log in) and password.',
    },
    billingInformation: {
        section: 'account', label: 'Billing Information', icon: 'fa-credit-card', path: '/account/billing',
        meta: 'Payment plans, payment methods, and receipts.',
        detail: 'Payment plan cards (plans with The Payment Option link to app.thepaymentoption.com via Manage Payment Plan) and a link to Receipts.',
    },
    notifications: {
        section: 'account', label: 'Notifications', icon: 'fa-bell', path: '/account/notifications',
        meta: 'E-mail and text message preferences.',
        detail: 'Turn optional e-mail topics and text messages on or off. Account messages (login codes, password resets, confirmations) always send.',
    },
    workSchedule: {
        section: 'account', label: 'Work Schedule', icon: 'fa-briefcase', path: '/account/schedule',
        meta: 'Your contracted work schedule.',
        detail: 'Declared contracted work hours; study time can not be logged inside them.',
    },
    certificationsOrganizations: {
        section: 'account', label: 'Certifications & Organizations', icon: 'fa-certificate', path: '/account/certs',
        meta: 'Certificate and organization IDs on file.',
        detail: 'One card per certification (Washington clock hours, WIAA, WSCA) with an on/off switch and its ID: OSPI certificate number, WIAA Membership ID (with Lookup WIAA Membership ID). Locked IDs are changed by staff.',
    },

    // ── Support ──────────────────────────────────────────────────────────
    helpCenter: {
        section: 'support', label: 'Help Center', icon: 'fa-circle-question', path: '/support',
        meta: 'Answers to common questions, organized by topic.',
        detail: 'Searchable answers by topic; each answer has its own link (/support/help/<id>).',
    },
    yourTickets: {
        section: 'support', label: 'Your Tickets', icon: 'fa-ticket', path: '/support/tickets',
        meta: 'Open and past support conversations.',
        detail: 'Every support conversation linked to the account (in-app, e-mail and text), with replies; each opens at /support/tickets/<id>.',
    },
    newTicket: {
        section: 'support', label: 'New Ticket', icon: 'fa-plus-circle', path: '/support/new',
        meta: 'Ask a question or report a problem.',
        detail: 'Send a message to support; suggests matching Help Center answers while typing.',
    },

    // ── Inside a course (/course/:id) ────────────────────────────────────
    courseInformation: {
        section: 'course', label: 'Course Information', icon: 'fa-chalkboard-teacher', path: '/course/:id/information/intro',
        meta: '',
        detail: 'Course start page: Begin, Continue or Review Course, plus cards for the study plan, reactivation, Certifications & Forms and finishing the course (Complete My Course / Keep My Course Open).',
    },
    courseStudyPlan: {
        section: 'course', label: 'Course Study Plan', icon: 'fa-clipboard-list-check', path: '/course/:id/information/setup',
        meta: 'View and develop your custom study plan.',
        audience: 'self-directed courses',
        detail: 'Four steps (Your Background, Goals & Objectives, Hour Allocation, Finalize Your Plan), then Submit for Approval; reviewed typically within 24-48 hours, and course work counts only once approved.',
    },
    courseOutline: {
        section: 'course', label: 'Course Outline', icon: 'fa-rectangle-list', path: '/course/:id/outline',
        meta: 'Access the online course and lessons.',
        detail: 'Lessons in order; Complete Lesson unlocks the next. Locked until a self-directed study plan is approved.',
    },
    courseNotes: {
        section: 'course', label: 'Online Course Notes', icon: 'fa-memo-pad', path: '/course/:id/information/notes',
        meta: 'View and edit online course notes.',
        detail: 'Notes that save automatically with the enrollment.',
    },
    courseFiles: {
        section: 'course', label: 'Files & Uploads', icon: 'fa-folder-arrow-up', path: '/course/:id/information/files',
        meta: 'Access course materials & upload your own attachments and work.',
        detail: 'Course materials and the student’s own uploads (up to 25 MB each).',
    },
    studyLog: {
        section: 'course', label: 'Study Log', icon: 'fa-clipboard-list-check', path: '/course/:id/information/log',
        meta: 'Log of online course activity, offline course work, and other qualifying activities.',
        detail: 'Logged time; Log Activity adds offline time (up to 4 hours per entry, not in the future, not before the course start, no overlaps, not in contracted hours).',
    },
    milestonesReflections: {
        section: 'course', label: 'Milestones & Reflections', icon: 'fa-flag-swallowtail', path: '/course/:id/information/reflections',
        meta: 'Track and reflect on your course progress.',
        audience: 'self-directed courses',
        detail: 'A reflection every 10 logged hours; logging pauses until it is accepted. Shows the rubric, reviewer feedback and Updated Reflection for changes.',
    },
    courseCertificationsForms: {
        section: 'course', label: 'Certifications & Forms', icon: 'fa-file-certificate', path: '/course/:id/information/applications/list',
        meta: 'Apply for certifications and retrieve official completion forms or certificates.',
        detail: "Where a student applies for this course's certifications (Required or optional, with any fee shown up front) and downloads awarded forms. This in-course page is named Certifications & Forms; the top-bar section is Certificates & Forms.",
    },
    yourEnrollment: {
        section: 'course', label: 'Your Enrollment', icon: 'fa-id-badge', path: '/course/:id/information/enrollment',
        meta: 'Enrollment details, payment records, and hour reallocation.',
        detail: 'Enrollment details, hours logged and remaining, payment records, and Reallocate Hours (self-directed courses: move unused hours into new courses; cannot be undone).',
    },

    // ── Inside a conference (/conference/:id) ───────────────────────────
    conferenceInformation: {
        section: 'conference', label: 'Conference Information', icon: 'fa-circle-info', path: '/conference/:id/information',
        meta: 'View conference information and instructions.',
        detail: 'Conference details and instructions from the organizer.',
    },
    sessionsSchedule: {
        section: 'conference', label: 'Sessions & Schedule', icon: 'fa-calendar-circle-user', path: '/conference/:id/schedule',
        meta: 'View, access and plan for the different sessions available.',
        detail: 'Sessions to add to the schedule and attend.',
    },
    onDemandContent: {
        section: 'conference', label: 'On-Demand Content', icon: 'fa-clapperboard-play', path: '/conference/:id/on-demand',
        meta: 'Watch content on-demand.',
        detail: 'Recorded sessions (e.g. Coaches School); time counts in full minutes of viewing.',
    },
    recordAttendance: {
        section: 'conference', label: 'Record & Attendance Log', icon: 'fa-clipboard-list-check', path: '/conference/:id/log',
        meta: 'Your log of attended sessions, on-demand content, and other qualifying activities.',
        detail: 'Confirm Attendance for each session after it ends; the log is the basis for credit.',
    },
    conferenceCertificationsForms: {
        section: 'conference', label: 'Certifications & Forms', icon: 'fa-file-certificate', path: '/conference/:id/applications/list',
        meta: 'Apply for certifications and retrieve official completion forms or certificates.',
        detail: "Where a student applies for this conference's certifications (WIAA certification, Washington clock hours, partner certifications), with any minimum minutes or sessions listed.",
    },
    yourRegistration: {
        section: 'conference', label: 'Your Registration', icon: 'fa-id-badge', path: '/conference/:id/registration',
        meta: 'Access your conference registration information.',
        detail: 'Registration details, receipt and Contact Organizer.',
    },
    purchaseOrder: {
        section: 'conference', label: 'Purchase Order (P.O.)', icon: 'fa-file-invoice', path: '/conference/:id/PO',
        meta: 'Manage the purchase order process.',
        audience: 'only while a purchase order is pending',
        detail: 'Save & Generate the P.O. request (invoice) for the district; the registration is not complete until the organizer receives the P.O.',
    },

    // ── Public pages (no login) ──────────────────────────────────────────
    login: {
        section: 'public', label: 'Login', path: '/',
        detail: 'Log in with e-mail or phone, then password, an e-mailed code or a texted code (5 digits, 30 minutes). Quick Access boxes take conference and course access codes.',
    },
    loginAssistance: {
        section: 'public', label: 'Login Assistance', path: '/assistance',
        detail: 'Try another login method, reset the password (link valid 30 minutes), or check whether an account exists.',
    },
    signUp: {
        section: 'public', label: 'Sign Up', path: '/signup',
        detail: 'Create a free account. One account per person; never sign up for someone else.',
    },
    wiaaWalkthrough: {
        section: 'public', label: 'WIAA Coach Walk-Through', path: '/wiaa-help',
        detail: 'Step-by-step guide for coaches: from FinalForms to enrolling, completing the WIAA certification application and syncing.',
    },
};

/** Menu item fields for a page: {label, icon, meta}. */
export function sitePage(key) {
    const page = SITE_PAGES[key];
    if (!page) throw new Error(`siteMap: unknown page ${key}`);
    return {label: page.label, icon: page.icon, meta: page.meta};
}
